/**
 * Copyright (c) 2025 Editoor Contributors
 *
 * Licensed under MIT License
 */

import { DebugLogger } from './debugLogger';
import { parseUrlFragment, createFastUrl } from './urlEncoder';

export interface BreadcrumbSegment {
    name: string;
    url: string;
    isExternal: boolean;
    icon?: string;
}

/**
 * Breadcrumbs received from shell (when running in iframe)
 * This is the source of truth for breadcrumbs when in shell mode
 */
let shellBreadcrumbs: BreadcrumbSegment[] | null = null;

/**
 * Listen for breadcrumb updates from shell
 */
if (typeof window !== 'undefined') {
    window.addEventListener('message', (event) => {
        // Security: only accept messages from same origin
        if (event.origin !== window.location.origin) return;

        if (event.data?.type === 'shell:breadcrumbs') {
            shellBreadcrumbs = event.data.payload.breadcrumbs || [];
        }
    });
}

/**
 * Resolve a relative URL to absolute based on current location
 */
function resolveUrl(url: string): string {
    const a = document.createElement('a');
    a.href = url;
    return a.href;
}

/**
 * Shared breadcrumb manager for consistent navigation across all pages
 */
export class BreadcrumbManager {
    /**
     * Check if breadcrumb stack contains any external items
     */
    static hasExternalItems(stack: BreadcrumbSegment[]): boolean {
        return stack.some(segment => segment.isExternal);
    }

    /**
     * Get the breadcrumb stack: shell message, then history.state, then the
     * `from` URL parameter, then a root crumb (or an empty stack).
     * Segments hold full document links, so they are kept in memory and history.state only.
     */
    static getBreadcrumb(currentDriveId?: string): BreadcrumbSegment[] {
        // Use breadcrumbs sent by shell (source of truth) when available
        if (shellBreadcrumbs !== null) {
            return shellBreadcrumbs;
        }

        // History state (browser back/forward)
        if (window.history.state?.breadcrumbs) {
            return window.history.state.breadcrumbs as BreadcrumbSegment[];
        }

        // URL parameter (federated navigation)
        const urlParams = new URLSearchParams(window.location.search);
        const fromParam = urlParams.get('from');

        if (fromParam) {
            try {
                const breadcrumbData = JSON.parse(atob(decodeURIComponent(fromParam)));
                const path = breadcrumbData.path || [];

                // Keep the trail in history.state and clean up the URL
                const url = new URL(window.location.href);
                url.searchParams.delete('from');
                // Merge with existing state (preserve editoorDoc, etc.)
                const existingState = window.history.state || {};
                window.history.replaceState({ ...existingState, breadcrumbs: path }, '', url.toString());

                return path;
            } catch {
                DebugLogger.log('error', 'Failed to parse breadcrumb from URL');
            }
        }

        // Default: empty stack or single root item
        if (currentDriveId) {
            // currentDriveId may include write token (e.g., "abc123-wt_xyz")
            const rootUrl = new URL(`drive.html#${currentDriveId}`, window.location.href);
            return [{
                name: 'My Drive',
                url: rootUrl.href,
                isExternal: false
            }];
        }

        return [];
    }

    /**
     * Keep the breadcrumb stack in history.state only.
     */
    static storeBreadcrumb(stack: BreadcrumbSegment[]): void {
        try {
            const existingState = window.history.state || {};
            window.history.replaceState({ ...existingState, breadcrumbs: stack }, '');
        } catch {
            DebugLogger.log('error', 'Failed to save breadcrumb');
        }
    }

    /**
     * Navigate with breadcrumb tracking via shell postMessage
     */
    static navigateWithBreadcrumb(targetUrl: string, newStack: BreadcrumbSegment[]): void {
        const absoluteUrl = resolveUrl(targetUrl);
        window.parent.postMessage({
            type: 'shell:navigate',
            payload: {
                url: absoluteUrl,
                fullStack: newStack
            }
        }, window.location.origin);
    }

    /**
     * Push a new segment onto the breadcrumb stack and navigate
     */
    static pushAndNavigate(name: string, url: string, isExternal: boolean = false): void {
        const currentStack = BreadcrumbManager.getBreadcrumb();
        const fullUrl = new URL(url, window.location.href);

        const newStack = [...currentStack, {
            name: name,
            url: fullUrl.href,
            isExternal: isExternal
        }];

        BreadcrumbManager.navigateWithBreadcrumb(url, newStack);
    }

    /**
     * Truncate breadcrumb stack and navigate (for clicking on breadcrumb segments)
     */
    static truncateAndNavigate(stack: BreadcrumbSegment[], index: number): void {
        const truncatedStack = stack.slice(0, index + 1);
        window.parent.postMessage({
            type: 'shell:navigateBack',
            payload: {
                index,
                breadcrumbs: truncatedStack
            }
        }, window.location.origin);
    }

    /**
     * Setup history state management for breadcrumbs
     * Shell handles history/popstate - this is a no-op
     */
    static setupHistoryState(_currentDriveId?: string): void {
        // Shell handles history/popstate
    }

    /**
     * Update the current breadcrumb name (e.g., when document title changes)
     * When running in shell, notifies shell to update its breadcrumb display
     */
    static updateCurrentName(name: string): void {
        window.parent.postMessage({
            type: 'shell:updateBreadcrumb',
            payload: { name }
        }, window.location.origin);
    }

    /**
     * Get a shareable URL for the current document
     * Format: https://origin#appType:docId
     */
    static getShareableUrl(): string {
        const origin = window.location.origin;

        // Extract app type from pathname
        const appTypeMatch = window.location.pathname.match(/^\/apps\/([a-z0-9-]+)\//i);
        const appType = appTypeMatch ? appTypeMatch[1] : 'drive';

        // Extract document ID from hash
        const hash = window.location.hash.substring(1);
        if (hash) {
            const { documentId, writeToken, appendOnlyToken } = parseUrlFragment(hash);
            return `${origin}#${createFastUrl(appType, documentId, writeToken, appendOnlyToken)}`;
        }

        // No hash - just return origin with app type
        return `${origin}#${appType}:`;
    }

    /**
     * Render breadcrumbs into a DOM element
     * Shared function to avoid duplicating rendering code
     * When running in shell, breadcrumbs are rendered by shell - we just send the icon
     */
    static render(currentItemIcon: string = '📄'): void {
        // Shell handles breadcrumb rendering - just send the current icon
        window.parent.postMessage({
            type: 'shell:updateBreadcrumb',
            payload: { icon: currentItemIcon }
        }, window.location.origin);
    }
}
